import StarSolid from '@eduzz/houston-icons/StarSolid';
import Table from '@eduzz/houston-ui/Table';
import TagHighlight from '@eduzz/houston-ui/Tag/Highlight';
import { Box, Button } from '@material-ui/core';
import Tooltip from '@material-ui/core/Tooltip';
import * as React from 'react';
import { useHistory } from 'react-router-dom';
import { VERTICAL_URL } from 'settings';
import Truncate from 'v2/components/Shared/TruncateText';
import { BANK_LIST } from 'v2/helpers/constants';

import type { IBankAccount } from 'v2/interfaces/financial';

import { useStyles } from './styles';
import { CancelReasonModal } from '../CancelReasonModal';
import { stepsReasonsId } from '../CancelReasonModal/constants';
import { ConfirmationModal } from '../ConfirmationModal';

import type { ReasonObj } from '../CancelReasonModal/constants';

export const MIN_BALANCE_TO_CREATE_ACCOUNT = 100;

type props = {
  isFidcAuthorized: boolean;
  currentBalance: number;
  businessName: string;
  bankDetails: IBankAccount[];
  isLoading: boolean;
  loadBankDetails: () => void;
};

function BankDetailsTable({ isFidcAuthorized, currentBalance, businessName, bankDetails, isLoading, loadBankDetails }: props) {
  const classes = useStyles();
  const history = useHistory();
  const [isOpenConfirmationModal, setIsOpenConfirmationModal] = React.useState(false);
  const [isOpenCancelReasonModal, setIsOpenCancelReasonModal] = React.useState(false);
  const [actionModal, setActionModal] = React.useState<'delete' | 'mainAccount' | null>(null);
  const [selectedAccount, setSelectedAccount] = React.useState<IBankAccount | null>(null);
  const [reason, setReason] = React.useState<null | ReasonObj>(null);
  const handleSetMainAccount = (data: IBankAccount) => {
    setActionModal('mainAccount');
    setSelectedAccount(data);
    toggleOpenConfirmationModal();
  };

  const handleDeleteAccount = (data: IBankAccount) => {
    setActionModal('delete');
    setSelectedAccount(data);
    toggleOpenConfirmationModal();
  };

  const handleShowCancelReason = (data: IBankAccount) => {
    setReason({
      bankAccountId: data.id,
      id: data.reason_disapproval_id || 0,
      description: data.reason_disapproval,
    });
    toggleOpenCancelReasonModal();
  };

  const handleGoToEditStep = (data: IBankAccount) => {
    const getErrorStep = (errorId: number) => {
      const { second, third } = stepsReasonsId;

      if (second.includes(errorId)) return '2';
      if (third.includes(errorId)) return '3';
      return '1';
    };

    sessionStorage.setItem('step_error', getErrorStep(data.id));

    history.push('/producer/profile/add-bank-account', {
      bankAccountId: data.id,
      description: null,
      step: null,
    });
  };

  const toggleOpenConfirmationModal = () => setIsOpenConfirmationModal((isOpen) => !isOpen);
  const toggleOpenCancelReasonModal = () => setIsOpenCancelReasonModal((isOpen) => !isOpen);

  const statusText = (statusSlug: string) => {
    if (statusSlug === 'active')
      return (
        <Tooltip title='A conta está aprovada e você pode torná-la padrão para saque' placement='top'>
          <div>
            <TagHighlight variant='outlined' color='feedbackColor.positive.pure'>
              Aprovada
            </TagHighlight>
          </div>
        </Tooltip>
      );
    if (statusSlug === 'canceled')
      return (
        <Tooltip title='Verifique o motivo de cancelamento e realize a correção do cadastro' placement='top'>
          <div>
            <TagHighlight variant='outlined' color='feedbackColor.negative.pure'>
              Cancelada
            </TagHighlight>
          </div>
        </Tooltip>
      );
    if (statusSlug === 'waiting_aprovation' || statusSlug === 'waiting_pdv')
      return (
        <Tooltip title='Edite o cadastro para realizar o processo de verificação' placement='top'>
          <div>
            <TagHighlight variant='outlined' color='neutralColor.low.dark'>
              Pendente
            </TagHighlight>
          </div>
        </Tooltip>
      );
    if (statusSlug === 'pending')
      return (
        <Tooltip title='A análise do cadastro está em andamento' placement='top'>
          <div>
            <TagHighlight variant='outlined' color='feedbackColor.warning.dark'>
              Em análise
            </TagHighlight>
          </div>
        </Tooltip>
      );
    return (
      <TagHighlight variant='outlined' color='neutralColor.low.dark'>
        {statusSlug}
      </TagHighlight>
    );
  };

  const handleToCompleteRegister = React.useCallback(() => {
    return (window.location.href = `${VERTICAL_URL}/profile/fidc`);
  }, []);

  const showRedirectToFidc = !isFidcAuthorized && currentBalance >= MIN_BALANCE_TO_CREATE_ACCOUNT && bankDetails?.length < 1;
  const showBalanceMessage = currentBalance < MIN_BALANCE_TO_CREATE_ACCOUNT && bankDetails?.length < 1;
  const showEmptyMessage = !!isFidcAuthorized && currentBalance >= MIN_BALANCE_TO_CREATE_ACCOUNT && bankDetails?.length < 1;

  return (
    <>
      {showRedirectToFidc && (
        <Box textAlign='center'>
          <p>
            <strong>{businessName}</strong>, você deve concluir a <strong>verificação de documentos</strong> para cadastrar sua conta bancária da
            Eduzz.
          </p>
          <Button variant='contained' className={classes.validateDocumentsButton} color='primary' onClick={handleToCompleteRegister}>
            Iniciar validação de documentos
          </Button>
        </Box>
      )}

      {showBalanceMessage && (
        <Box textAlign='center'>
          <p>
            <strong>{businessName}</strong>, para realizar o cadastro de uma conta bancária, é obrigatório ter um saldo de pelo menos{' '}
            <strong>R$ {MIN_BALANCE_TO_CREATE_ACCOUNT},00</strong> em sua conta Eduzz.
          </p>
        </Box>
      )}

      {showEmptyMessage && (
        <Box textAlign='center'>
          <p>
            <strong>{businessName}</strong>, você ainda não cadastrou nenhuma conta bancária na Eduzz. Toque no botão acima e{' '}
            <strong>cadastre uma conta agora</strong> mesmo.
          </p>
        </Box>
      )}

      {!showRedirectToFidc && !showBalanceMessage && !showEmptyMessage && (
        <>
          <Table loading={isLoading} total={bankDetails?.length}>
            {!isLoading && bankDetails?.length > 0 && (
              <Table.Header>
                <Table.Column>Titular</Table.Column>
                <Table.Column>Banco</Table.Column>
                <Table.Column width={40}>Agência</Table.Column>
                <Table.Column width={40}>Conta</Table.Column>
                <Table.Column></Table.Column>
                <Table.Column>Moeda</Table.Column>
                <Table.Column>Tipo de conta</Table.Column>
                <Table.Column width={180}>Status</Table.Column>
              </Table.Header>
            )}
            <Table.Body>
              {/* TODO adicionar empty state */}
              {/* <Table.Empty count={bankDetails?.length || 0}>
            <EmptyState />
          </Table.Empty> */}
              {bankDetails?.map((entry: IBankAccount, index: number) => {
                const agency = entry.agency_digit ? `${entry.agency}-${entry.agency_digit}` : `${entry.agency}`;
                const account = entry.account_digit ? `${entry.account}-${entry.account_digit}` : `${entry.account}`;

                return (
                  <Table.Row className={classes.tableRow} data={entry} index={index} key={`BankDetail-${entry.id}`}>
                    <Table.Cell>
                      <Truncate count={15} final='...'>
                        {entry.name}
                      </Truncate>
                    </Table.Cell>
                    <Table.Cell>
                      <Truncate count={20} final='...'>
                        {BANK_LIST[entry.bank_id] ?? '-'}
                      </Truncate>
                    </Table.Cell>
                    <Table.Cell>
                      <Truncate count={9} final='...'>
                        {agency}
                      </Truncate>
                    </Table.Cell>
                    <Table.Cell>
                      <Truncate count={9} final='...'>
                        {account}
                      </Truncate>
                    </Table.Cell>
                    <Table.Cell>{entry.main && <StarSolid size='lg' className={classes.starColor} />}</Table.Cell>
                    <Table.Cell>{entry.currency?.code}</Table.Cell>
                    <Table.Cell>{entry.type}</Table.Cell>
                    <Table.Cell>{statusText(entry.status_slug)}</Table.Cell>
                    {entry.status_slug === 'canceled' && <Table.Action onClick={handleShowCancelReason}>Ver motivo</Table.Action>}
                    {entry.status_slug === 'waiting_aprovation' && <Table.Action onClick={handleGoToEditStep}>Editar</Table.Action>}
                    <Table.Action onClick={handleSetMainAccount} disabled={entry.main}>
                      {/* TODO Adicionar opção de ícone */}
                      {/* <StarIcon className={`${classes.menuIcon} ${classes.starColor}`} />  */}
                      Tornar conta principal
                    </Table.Action>
                    <Table.Action onClick={handleDeleteAccount}>
                      {/* TODO Adicionar opção de ícone
                {/* <TrashSolidIcon className={classes.menuIcon} /> */}
                      Excluir conta
                    </Table.Action>
                  </Table.Row>
                );
              })}
            </Table.Body>
          </Table>
          <ConfirmationModal
            isOpen={isOpenConfirmationModal}
            setClose={toggleOpenConfirmationModal}
            action={actionModal}
            account={selectedAccount}
            loadData={loadBankDetails}
          />
          <CancelReasonModal isOpen={isOpenCancelReasonModal} setClose={toggleOpenCancelReasonModal} reason={reason} />
        </>
      )}
    </>
  );
}

export { BankDetailsTable };
